import {
  type AamsServerRegistrationFormInput,
  type AamsServerRegistrationFormOutput,
  AnsielApiRegistrationFormInput,
  type AnsielApiRegistrationFormOutput,
  DATA_RECEIVER_PATH,
  PROVIDER_TYPE,
  getAamsServerRegistrationFormSchema,
  getAnsielApiRegistrationFormSchema,
  parse,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuPlus, LuTrash } from "react-icons/lu";
import { type Merge } from "type-fest";
import {
  Button,
  DropDownList,
  FacilitySelector,
  FormFieldContainer,
  FormFieldInput,
  Input,
  OrganizationSelector,
  PageName,
  buttonStyles,
  formFieldInputStyles,
  iconButtonStyles,
  showError,
  typographyStyles,
} from "#components";
import {
  $alert,
  $confirm,
  createProvider,
  useFacilityInfiniteQuery,
  useGetErrorMessage,
  useOrganizationInfiniteQuery,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/management/providers/registration")({
  component: Component,
});

function Component() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [providerType, setProviderType] = useState(PROVIDER_TYPE.AAMS_SERVER);

  const handleSubmit = useCallback(
    async function (output: AamsServerRegistrationFormOutput | AnsielApiRegistrationFormOutput) {
      try {
        console.debug("フォームが送信されました。", output);
        const { provider } = await createProvider({
          ...output,
          facilityKey: output.facility?.facilityKey,
          organizationKey: output.organization?.organizationKey,
        });

        await $alert({
          color: "success",
          content: t("%Register.2%", { $: { name: provider.name } }),
          title: t("%Register.0%"),
        });

        await navigate({ to: "/management/providers", ignoreBlocker: true });
      } catch (error) {
        showError(error);
      }
    },
    [navigate, t],
  );

  return (
    <main>
      <PageName>{t("%ProviderInformation.0%")}</PageName>
      <p className={css({ fontSize: "24px", fontWeight: 700 })}>{t("%ProviderInformation.0%")}</p>

      <DropDownList
        className={css({ marginTop: "16px", maxWidth: "320px" })}
        onChange={function (event) {
          setProviderType(event.target.value);
        }}
        value={providerType}
      >
        {[PROVIDER_TYPE.AAMS_SERVER, PROVIDER_TYPE.ANSIEL_API].map(function (providerType) {
          return (
            <option key={providerType} value={providerType}>
              {t("%ProviderType." + providerType + ".0%")}
            </option>
          );
        })}
      </DropDownList>

      {providerType === PROVIDER_TYPE.AAMS_SERVER ? (
        <AamsServerRegistrationForm onSubmit={handleSubmit} />
      ) : providerType === PROVIDER_TYPE.ANSIEL_API ? (
        <AnsielApiRegistrationForm onSubmit={handleSubmit} />
      ) : (
        <p className={css({ marginTop: "16px" })}>{t("%z%")}</p>
      )}
    </main>
  );
}

// =============================================================
// AAMSサーバー登録フォーム
// =============================================================

type AamsServerRegistrationFormProperties = Merge<
  React.ComponentProps<"form">,
  {
    onSubmit: { (output: AamsServerRegistrationFormOutput): Promise<void> };
  }
>;
function AamsServerRegistrationForm({
  className,
  onSubmit,
  ...properties
}: AamsServerRegistrationFormProperties) {
  const getErrorMessage = useGetErrorMessage();
  const { t } = useTranslation();

  const schema = getAamsServerRegistrationFormSchema();
  const defaultValues = useMemo(function (): AamsServerRegistrationFormInput {
    return { facility: null, name: "", organization: null, type: PROVIDER_TYPE.AAMS_SERVER };
  }, []);

  const { Field, Subscribe, handleSubmit, setFieldValue, ...form } = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      const output = parse(schema, input);
      await onSubmit(output);
    },
    validators: { onSubmit: schema },
  });

  const organization = useStore(form.store, (state) => state.values.organization);
  const submitting = useStore(form.store, (state) => state.isSubmitting);

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: t("%Form.1%"),
              no: t("%Stay.0%"),
              title: t("%Form.0%"),
              yes: t("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  const organizationInfiniteQuery = useOrganizationInfiniteQuery({ limit: 25 });
  const organizations = useMemo(
    function () {
      return (
        organizationInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.organizations;
        }) ?? []
      );
    },
    [organizationInfiniteQuery.data],
  );

  const facilityInfiniteQuery = useFacilityInfiniteQuery(
    organization?.organizationKey
      ? {
          index: "organizationKey-deleted-gsi",
          condition: [["organizationKey", "=", organization.organizationKey]],
          limit: 25,
        }
      : { limit: 25 },
  );
  const facilities = useMemo(
    function () {
      return (
        facilityInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.facilities;
        }) ?? []
      );
    },
    [facilityInfiniteQuery.data],
  );

  return (
    <form
      {...properties}
      className={cx(
        css({ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }),
        className,
      )}
      onSubmit={function (event) {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <Field
        children={function (field) {
          return (
            <Input
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              id={field.name}
              label={t("%ProviderName.0%")}
              onBlur={() => field.handleBlur()}
              onChange={(event) => field.handleChange(event.target.value)}
              required
              title=""
              value={field.state.value}
            />
          );
        }}
        name="name"
      />
      <Field
        children={function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              invalid={field.state.meta.errors.length > 0}
              label={t("%Organization.0%")}
            >
              <OrganizationSelector
                className={css({ maxWidth: "320px", width: "100%" })}
                load={async function () {
                  await organizationInfiniteQuery.fetchNextPage();
                }}
                loaded={!organizationInfiniteQuery.hasNextPage}
                loading={organizationInfiniteQuery.isFetching}
                onBlur={field.handleBlur}
                onChange={function (organization) {
                  if (field.state.value?.organizationKey !== organization?.organizationKey) {
                    setFieldValue("facility", null);
                  }
                  field.handleChange(organization);
                }}
                options={organizations}
                title={t("%Organization.0%")}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
        name="organization"
      />
      <Field
        children={function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              invalid={field.state.meta.errors.length > 0}
              label={t("%Facility.0%") + (organization ? " (" + organization.name + ")" : "")}
            >
              <FacilitySelector
                className={css({ maxWidth: "320px", width: "100%" })}
                load={async function () {
                  await facilityInfiniteQuery.fetchNextPage();
                }}
                loaded={!facilityInfiniteQuery.hasNextPage}
                loading={facilityInfiniteQuery.isFetching}
                onBlur={field.handleBlur}
                onChange={function (facility) {
                  field.handleChange(facility);
                }}
                options={facilities}
                title={t("%Facility.0%") + (organization ? " (" + organization.name + ")" : "")}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
        name="facility"
      />

      <div className={css({ display: "flex", gap: "8px" })}>
        <Link
          className={buttonStyles({ color: "secondary" })}
          disabled={submitting}
          to="/management/providers"
        >
          {t("%Cancel.0%")}
        </Link>
        <Subscribe
          selector={function ({ canSubmit }) {
            return canSubmit;
          }}
        >
          {function (canSubmit) {
            return (
              <Button
                className={css({ width: "max-content" })}
                disabled={!canSubmit || submitting}
                type="submit"
              >
                {t("%Save.0%")}
              </Button>
            );
          }}
        </Subscribe>
      </div>
    </form>
  );
}

// =============================================================
// ANSIEL API登録フォーム
// =============================================================

type AnsielApiRegistrationFormProperties = Merge<
  React.ComponentProps<"form">,
  {
    onSubmit: { (output: AnsielApiRegistrationFormOutput): Promise<void> };
  }
>;
function AnsielApiRegistrationForm({
  className,
  onSubmit,
  ...properties
}: AnsielApiRegistrationFormProperties) {
  const getErrorMessage = useGetErrorMessage();
  const { t } = useTranslation();

  const schema = getAnsielApiRegistrationFormSchema();
  const defaultValues = useMemo(function (): AnsielApiRegistrationFormInput {
    return {
      apiKey: "",
      entryPoint: "",
      facility: null,
      name: "",
      organization: null,
      organizationCode: "",
      password: "",
      relayTo: { [DATA_RECEIVER_PATH.ANSIEL_PUSH_NOTIFICATION]: [] },
      type: PROVIDER_TYPE.ANSIEL_API,
    };
  }, []);

  const { Field, Subscribe, handleSubmit, setFieldValue, ...form } = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      const output = parse(schema, input);
      await onSubmit(output);
    },
    validators: { onSubmit: schema },
  });

  const organization = useStore(form.store, (state) => state.values.organization);
  const submitting = useStore(form.store, (state) => state.isSubmitting);

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: t("%Form.1%"),
              no: t("%Stay.0%"),
              title: t("%Form.0%"),
              yes: t("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  const organizationInfiniteQuery = useOrganizationInfiniteQuery({ limit: 25 });
  const organizations = useMemo(
    function () {
      return (
        organizationInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.organizations;
        }) ?? []
      );
    },
    [organizationInfiniteQuery.data],
  );

  const facilityInfiniteQuery = useFacilityInfiniteQuery(
    organization?.organizationKey
      ? {
          index: "organizationKey-deleted-gsi",
          condition: [["organizationKey", "=", organization.organizationKey]],
          limit: 25,
        }
      : { limit: 25 },
  );
  const facilities = useMemo(
    function () {
      return (
        facilityInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.facilities;
        }) ?? []
      );
    },
    [facilityInfiniteQuery.data],
  );

  return (
    <form
      {...properties}
      className={cx(
        css({ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }),
        className,
      )}
      onSubmit={function (event) {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <Field name="name" validators={{ onChange: schema.entries.name }}>
        {function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              invalid={!field.state.meta.isValid}
              label={t("%ProviderName.0%")}
              required
            >
              <FormFieldInput
                className={css(formFieldInputStyles.raw({}), { width: "100%" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value);
                }}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
      </Field>

      <Field name="apiKey" validators={{ onChange: schema.entries.apiKey }}>
        {function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              invalid={!field.state.meta.isValid}
              label={t("%ApiKey.0%")}
              required
            >
              <FormFieldInput
                className={css(formFieldInputStyles.raw({}), { width: "100%" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value);
                }}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
      </Field>

      <Field name="password" validators={{ onChange: schema.entries.password }}>
        {function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              invalid={!field.state.meta.isValid}
              label={t("%Password.0%")}
              required
            >
              <FormFieldInput
                className={css(formFieldInputStyles.raw({}), { width: "100%" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value);
                }}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
      </Field>

      <Field name="entryPoint" validators={{ onChange: schema.entries.entryPoint }}>
        {function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              description={t("%EntryPoint.1%")}
              invalid={!field.state.meta.isValid}
              label={t("%EntryPoint.0%")}
              required
            >
              <FormFieldInput
                className={css(formFieldInputStyles.raw({}), { width: "100%" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value);
                }}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
      </Field>

      <Field name="organizationCode" validators={{ onChange: schema.entries.organizationCode }}>
        {function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              description={t("%OrganizationCode.1%")}
              invalid={!field.state.meta.isValid}
              label={t("%OrganizationCode.0%")}
              required
            >
              <FormFieldInput
                className={css(formFieldInputStyles.raw({}), { width: "100%" })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value);
                }}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
      </Field>

      <Field mode="array" name={`relayTo.${DATA_RECEIVER_PATH.ANSIEL_PUSH_NOTIFICATION}`}>
        {function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              description={t("%DataRelayDestination.1%")}
              invalid={!field.state.meta.isValid}
              label={t("%DataRelayDestination.0%")}
              required
            >
              {field.state.value.map(function (_, index) {
                return (
                  <Field
                    key={index}
                    name={`relayTo.${DATA_RECEIVER_PATH.ANSIEL_PUSH_NOTIFICATION}[${index}]`}
                  >
                    {function (subfield) {
                      return (
                        <FormFieldContainer
                          caution={getErrorMessage(subfield.state.meta.errors.at(0))}
                          className={css({ width: "100%" })}
                          invalid={!subfield.state.meta.isValid}
                        >
                          <div className={css({ display: "flex", gap: "8px", width: "100%" })}>
                            <FormFieldInput
                              className={css(formFieldInputStyles.raw({}), { width: "100%" })}
                              id={subfield.name}
                              onBlur={function () {
                                subfield.handleBlur();
                              }}
                              onChange={function (event) {
                                subfield.handleChange(event.target.value);
                              }}
                              value={subfield.state.value}
                            />
                            <button
                              className={iconButtonStyles({ color: "ghost", size: "40px" })}
                              onClick={function (event) {
                                event.stopPropagation();
                                event.preventDefault();
                                field.handleChange(field.state.value.filter((_, j) => j !== index));
                              }}
                            >
                              <LuTrash />
                            </button>
                          </div>
                        </FormFieldContainer>
                      );
                    }}
                  </Field>
                );
              })}

              <div
                className={css(typographyStyles.raw({ fontSize: "14px", lineHeight: 1 }), {
                  alignItems: "center",
                  cursor: "pointer",
                  display: "flex",
                  gap: "8px",
                  marginTop: "8px",
                })}
                onClick={function () {
                  field.pushValue("");
                }}
              >
                <LuPlus />
                <p>{t("%Add.0%")}</p>
              </div>
            </FormFieldContainer>
          );
        }}
      </Field>

      <Field name="organization">
        {function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              invalid={field.state.meta.errors.length > 0}
              label={t("%Organization.0%")}
            >
              <OrganizationSelector
                className={css({ maxWidth: "320px", width: "100%" })}
                load={async function () {
                  await organizationInfiniteQuery.fetchNextPage();
                }}
                loaded={!organizationInfiniteQuery.hasNextPage}
                loading={organizationInfiniteQuery.isFetching}
                onBlur={field.handleBlur}
                onChange={function (organization) {
                  if (field.state.value?.organizationKey !== organization?.organizationKey) {
                    setFieldValue("facility", null);
                  }
                  field.handleChange(organization);
                }}
                options={organizations}
                title={t("%Organization.0%")}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
      </Field>
      <Field name="facility">
        {function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              invalid={field.state.meta.errors.length > 0}
              label={t("%Facility.0%") + (organization ? " (" + organization.name + ")" : "")}
            >
              <FacilitySelector
                className={css({ maxWidth: "320px", width: "100%" })}
                load={async function () {
                  await facilityInfiniteQuery.fetchNextPage();
                }}
                loaded={!facilityInfiniteQuery.hasNextPage}
                loading={facilityInfiniteQuery.isFetching}
                onBlur={field.handleBlur}
                onChange={function (facility) {
                  field.handleChange(facility);
                }}
                options={facilities}
                title={t("%Facility.0%") + (organization ? " (" + organization.name + ")" : "")}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
      </Field>

      <div className={css({ display: "flex", gap: "8px" })}>
        <Link
          className={buttonStyles({ color: "secondary" })}
          disabled={submitting}
          to="/management/providers"
        >
          {t("%Cancel.0%")}
        </Link>
        <Subscribe
          selector={function ({ canSubmit }) {
            return canSubmit;
          }}
        >
          {function (canSubmit) {
            return (
              <Button
                className={css({ width: "max-content" })}
                disabled={!canSubmit || submitting}
                type="submit"
              >
                {t("%Save.0%")}
              </Button>
            );
          }}
        </Subscribe>
      </div>
    </form>
  );
}
